import {
  createConversation,
  DEFAULT_ASSISTANTS,
  DEFAULT_SETTINGS,
  normalizeAssistant,
  normalizeQuestionMode,
} from "./defaults";
import { isChatModeId } from "./modes";
import type {
  AppSettings,
  Artifact,
  AssistantPersona,
  Attachment,
  ChatMessage,
  Conversation,
  PortableArchive,
  Project,
  QuestionSession,
} from "./types";

export const DATABASE_NAME = "vesper-ai";
export const DATABASE_VERSION = 3;

export const STORE_NAMES = [
  "conversations",
  "messages",
  "projects",
  "assistants",
  "artifacts",
  "attachments",
  "settings",
  "agentConfigurations",
  "questionSessions",
  "metadata",
] as const;

type StoreName = (typeof STORE_NAMES)[number];

interface StoredMessage extends ChatMessage {
  conversationId: string;
  order: number;
}

interface StoredAttachment extends Attachment {
  conversationId: string;
  messageId: string;
  createdAt: number;
}

interface StoredSetting {
  id: "app";
  value: AppSettings;
}

/** Schema v1 stored a single artifact per conversation, keyed by conversationId. */
interface LegacyArtifactRecord {
  conversationId: string;
  title: string;
  content: string;
  language: string;
  updatedAt: number;
}

let databasePromise: Promise<IDBDatabase> | undefined;

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(new Error(request.error?.message ?? "Operazione IndexedDB fallita."));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () =>
      reject(
        new Error(
          transaction.error?.message ??
            "Transazione IndexedDB annullata. I dati in memoria sono stati conservati.",
        ),
      );
    transaction.onerror = () => {
      // onabort provides the actionable transaction error.
    };
  });
}

function createIndex(
  store: IDBObjectStore,
  name: string,
  keyPath: string | string[],
  options?: IDBIndexParameters,
): void {
  if (!store.indexNames.contains(name)) store.createIndex(name, keyPath, options);
}

function createArtifactStore(db: IDBDatabase): IDBObjectStore {
  const store = db.createObjectStore("artifacts", { keyPath: "id" });
  store.createIndex("conversationId", "conversationId");
  store.createIndex("messageId", "messageId");
  store.createIndex("updatedAt", "updatedAt");
  return store;
}

/**
 * v1 keyed artifacts by conversationId and held exactly one per conversation.
 * v2 keys them by their own id so a conversation can own several. The old rows
 * are read before the store is dropped and re-inserted in the new shape, inside
 * the same versionchange transaction, so an interrupted upgrade rolls back
 * rather than losing the user's documents.
 */
function upgradeArtifactStore(db: IDBDatabase, transaction: IDBTransaction): void {
  if (!db.objectStoreNames.contains("artifacts")) {
    createArtifactStore(db);
    return;
  }
  const existing = transaction.objectStore("artifacts");
  if (existing.keyPath === "id") {
    createIndex(existing, "conversationId", "conversationId");
    createIndex(existing, "messageId", "messageId");
    createIndex(existing, "updatedAt", "updatedAt");
    return;
  }
  const legacy = existing.getAll();
  legacy.onsuccess = () => {
    const rows = (legacy.result ?? []) as LegacyArtifactRecord[];
    db.deleteObjectStore("artifacts");
    const store = createArtifactStore(db);
    for (const row of rows) {
      if (!row?.conversationId) continue;
      store.put({
        id: crypto.randomUUID(),
        conversationId: row.conversationId,
        title: row.title || "Artifact",
        kind: row.language === "markdown" ? "markdown" : "code",
        language: row.language || "markdown",
        content: row.content ?? "",
        revisions: [],
        createdAt: row.updatedAt ?? Date.now(),
        updatedAt: row.updatedAt ?? Date.now(),
      } satisfies Artifact);
    }
  };
}

export function openVesperDatabase(): Promise<IDBDatabase> {
  if (databasePromise) return databasePromise;
  if (!globalThis.indexedDB) {
    return Promise.reject(
      new Error("IndexedDB non è disponibile in questo browser."),
    );
  }
  databasePromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onblocked = () => {
      databasePromise = undefined;
      reject(
        new Error(
          "Il database è bloccato da un'altra scheda. Chiudi le altre schede di Vesper e riprova.",
        ),
      );
    };
    request.onerror = () => {
      databasePromise = undefined;
      reject(
        new Error(
          request.error?.message ?? "Apertura del database IndexedDB fallita.",
        ),
      );
    };
    request.onupgradeneeded = () => {
      const db = request.result;
      try {
        const conversations = db.objectStoreNames.contains("conversations")
          ? request.transaction!.objectStore("conversations")
          : db.createObjectStore("conversations", { keyPath: "id" });
        createIndex(conversations, "projectId", "projectId");
        createIndex(conversations, "createdAt", "createdAt");
        createIndex(conversations, "updatedAt", "updatedAt");
        createIndex(conversations, "updatedAtId", ["updatedAt", "id"]);

        const messages = db.objectStoreNames.contains("messages")
          ? request.transaction!.objectStore("messages")
          : db.createObjectStore("messages", { keyPath: "id" });
        createIndex(messages, "conversationId", "conversationId");
        createIndex(messages, "branchId", "branchId");
        createIndex(messages, "parentId", "parentId");
        createIndex(messages, "createdAt", "createdAt");
        createIndex(messages, "status", "status");
        createIndex(messages, "order", "order");
        createIndex(messages, "conversationOrder", ["conversationId", "order"], {
          unique: true,
        });

        const projects = db.objectStoreNames.contains("projects")
          ? request.transaction!.objectStore("projects")
          : db.createObjectStore("projects", { keyPath: "id" });
        createIndex(projects, "createdAt", "createdAt");

        const assistants = db.objectStoreNames.contains("assistants")
          ? request.transaction!.objectStore("assistants")
          : db.createObjectStore("assistants", { keyPath: "id" });
        createIndex(assistants, "createdAt", "createdAt");

        upgradeArtifactStore(db, request.transaction!);

        const attachments = db.objectStoreNames.contains("attachments")
          ? request.transaction!.objectStore("attachments")
          : db.createObjectStore("attachments", { keyPath: "id" });
        createIndex(attachments, "conversationId", "conversationId");
        createIndex(attachments, "messageId", "messageId");
        createIndex(attachments, "createdAt", "createdAt");

        if (!db.objectStoreNames.contains("settings")) {
          db.createObjectStore("settings", { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains("agentConfigurations")) {
          db.createObjectStore("agentConfigurations", { keyPath: "id" });
        }

        const questionSessions = db.objectStoreNames.contains("questionSessions")
          ? request.transaction!.objectStore("questionSessions")
          : db.createObjectStore("questionSessions", { keyPath: "id" });
        createIndex(questionSessions, "conversationId", "conversationId", {
          unique: true,
        });
        createIndex(questionSessions, "status", "status");
        createIndex(questionSessions, "updatedAt", "updatedAt");

        if (!db.objectStoreNames.contains("metadata")) {
          db.createObjectStore("metadata", { keyPath: "id" });
        }
      } catch (error) {
        request.transaction?.abort();
        reject(
          new Error(
            `Upgrade IndexedDB fallito: ${
              error instanceof Error ? error.message : String(error)
            }`,
          ),
        );
      }
    };
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => {
        db.close();
        databasePromise = undefined;
      };
      resolve(db);
    };
  });
  return databasePromise;
}

/**
 * Messages live in their own store, so the conversation record carries a plain
 * text digest instead. Sidebar search runs over every conversation, including
 * the ones whose messages have never been paged in.
 */
export function buildSearchIndex(conversation: Conversation): string {
  return [conversation.title, ...conversation.messages.map((message) => message.content)]
    .join(" ")
    .toLowerCase()
    .slice(0, 20_000);
}

function conversationRecord(conversation: Conversation): Omit<Conversation, "messages"> {
  const { messages: _messages, ...record } = conversation;
  return {
    ...record,
    messagesLoaded: false,
    searchIndex: conversation.messagesLoaded
      ? buildSearchIndex(conversation)
      : (conversation.searchIndex ?? conversation.title.toLowerCase()),
  };
}

function readConversation(value: unknown): Conversation {
  const record = value as Partial<Conversation> & { id: string };
  return {
    ...(record as Conversation),
    messages: [],
    messagesLoaded: false,
    mode: isChatModeId(record.mode) ? record.mode : "standard",
    tags: record.tags ?? [],
    searchIndex: record.searchIndex ?? (record.title ?? "").toLowerCase(),
    questionMode: normalizeQuestionMode(record.questionMode),
  };
}

function lightweightAttachments(
  attachments?: Attachment[],
): Attachment[] | undefined {
  return attachments?.map(({ dataUrl: _dataUrl, extractedText: _text, ...item }) => item);
}

function storedMessage(
  conversationId: string,
  message: ChatMessage,
  order: number,
): StoredMessage {
  const { attachments, ...rest } = message;
  return {
    ...rest,
    conversationId,
    order,
    ...(attachments
      ? { attachments: lightweightAttachments(attachments)! }
      : {}),
  };
}

export async function loadBootstrap(pageSize = 30): Promise<{
  conversations: Conversation[];
  projects: Project[];
  assistants: AssistantPersona[];
  settings: AppSettings;
  questionSessions: QuestionSession[];
}> {
  const db = await openVesperDatabase();
  const transaction = db.transaction(
    ["conversations", "projects", "assistants", "settings", "questionSessions"],
    "readonly",
  );
  const conversationsStore = transaction.objectStore("conversations");
  const conversations: Conversation[] = [];
  await new Promise<void>((resolve, reject) => {
    const cursor = conversationsStore.index("updatedAtId").openCursor(null, "prev");
    cursor.onerror = () =>
      reject(new Error(cursor.error?.message ?? "Paginazione conversazioni fallita."));
    cursor.onsuccess = () => {
      const result = cursor.result;
      if (!result || conversations.length >= pageSize) {
        resolve();
        return;
      }
      conversations.push(readConversation(result.value));
      result.continue();
    };
  });
  const [projects, assistants, setting, questionSessions] = await Promise.all([
    requestResult(transaction.objectStore("projects").getAll()) as Promise<Project[]>,
    requestResult(transaction.objectStore("assistants").getAll()) as Promise<
      AssistantPersona[]
    >,
    requestResult(transaction.objectStore("settings").get("app")) as Promise<
      StoredSetting | undefined
    >,
    requestResult(transaction.objectStore("questionSessions").getAll()) as Promise<
      QuestionSession[]
    >,
  ]);
  await transactionDone(transaction);
  return {
    conversations,
    projects,
    assistants: assistants.length
      ? assistants.map(normalizeAssistant)
      : DEFAULT_ASSISTANTS,
    settings: setting?.value ?? DEFAULT_SETTINGS,
    questionSessions,
  };
}

export async function loadConversationPage(
  beforeUpdatedAt: number,
  beforeIdOrPageSize?: string | number,
  pageSize = 30,
): Promise<Conversation[]> {
  const db = await openVesperDatabase();
  const transaction = db.transaction("conversations", "readonly");
  const conversations: Conversation[] = [];
  const beforeId =
    typeof beforeIdOrPageSize === "string" ? beforeIdOrPageSize : undefined;
  const requestedPageSize =
    typeof beforeIdOrPageSize === "number" ? beforeIdOrPageSize : pageSize;
  await new Promise<void>((resolve, reject) => {
    const index = transaction.objectStore("conversations").index(
      beforeId ? "updatedAtId" : "updatedAt",
    );
    const range = beforeId
      ? IDBKeyRange.upperBound([beforeUpdatedAt, beforeId], true)
      : IDBKeyRange.upperBound(beforeUpdatedAt, true);
    const cursor = index.openCursor(range, "prev");
    cursor.onerror = () =>
      reject(new Error(cursor.error?.message ?? "Paginazione conversazioni fallita."));
    cursor.onsuccess = () => {
      const result = cursor.result;
      if (!result || conversations.length >= requestedPageSize) {
        resolve();
        return;
      }
      conversations.push(readConversation(result.value));
      result.continue();
    };
  });
  await transactionDone(transaction);
  return conversations;
}

export async function loadConversationMessages(
  conversationId: string,
): Promise<{ messages: ChatMessage[]; artifacts: Artifact[] }> {
  const db = await openVesperDatabase();
  const transaction = db.transaction(["messages", "artifacts"], "readonly");
  const range = IDBKeyRange.bound(
    [conversationId, 0],
    [conversationId, Number.MAX_SAFE_INTEGER],
  );
  const records = (await requestResult(
    transaction.objectStore("messages").index("conversationOrder").getAll(range),
  )) as StoredMessage[];
  const artifacts = (await requestResult(
    transaction.objectStore("artifacts").index("conversationId").getAll(conversationId),
  )) as Artifact[];
  await transactionDone(transaction);
  return {
    messages: records.map(
      ({ conversationId: _conversationId, order: _order, ...message }) => message,
    ),
    artifacts: artifacts.sort((left, right) => left.createdAt - right.createdAt),
  };
}

export async function saveArtifact(artifact: Artifact): Promise<void> {
  const db = await openVesperDatabase();
  const transaction = db.transaction("artifacts", "readwrite");
  transaction.objectStore("artifacts").put(artifact);
  await transactionDone(transaction);
}

export async function deleteArtifactData(id: string): Promise<void> {
  const db = await openVesperDatabase();
  const transaction = db.transaction("artifacts", "readwrite");
  transaction.objectStore("artifacts").delete(id);
  await transactionDone(transaction);
}

export async function hydrateMessageAttachments(
  messages: ChatMessage[],
): Promise<ChatMessage[]> {
  const ids = messages.flatMap((message) =>
    (message.attachments ?? [])
      .filter((attachment) => !attachment.dataUrl && !attachment.extractedText)
      .map((attachment) => attachment.id),
  );
  if (!ids.length) return messages;
  const db = await openVesperDatabase();
  const transaction = db.transaction("attachments", "readonly");
  const records = await Promise.all(
    ids.map((id) => requestResult(transaction.objectStore("attachments").get(id))),
  );
  await transactionDone(transaction);
  const byId = new Map(
    records
      .filter(Boolean)
      .map((record) => {
        const {
          conversationId: _conversationId,
          messageId: _messageId,
          createdAt: _createdAt,
          ...attachment
        } = record as StoredAttachment;
        return [attachment.id, attachment] as const;
      }),
  );
  return messages.map((message) => ({
    ...message,
    ...(message.attachments
      ? {
          attachments: message.attachments.map(
            (attachment) => byId.get(attachment.id) ?? attachment,
          ),
        }
      : {}),
  }));
}

export async function saveSettings(settings: AppSettings): Promise<void> {
  const db = await openVesperDatabase();
  const transaction = db.transaction(
    ["settings", "agentConfigurations"],
    "readwrite",
  );
  transaction.objectStore("settings").put({ id: "app", value: settings });
  transaction
    .objectStore("agentConfigurations")
    .put({ id: "active", value: settings.agentTeam });
  await transactionDone(transaction);
}

export async function saveConversation(conversation: Conversation): Promise<void> {
  const db = await openVesperDatabase();
  const transaction = db.transaction("conversations", "readwrite");
  transaction.objectStore("conversations").put(conversationRecord(conversation));
  await transactionDone(transaction);
}

export async function saveMessage(
  conversationId: string,
  message: ChatMessage,
  order: number,
): Promise<void> {
  const db = await openVesperDatabase();
  const transaction = db.transaction(["messages", "attachments"], "readwrite");
  const attachmentStore = transaction.objectStore("attachments");
  const incomingAttachmentIds = new Set(
    (message.attachments ?? []).map((attachment) => attachment.id),
  );
  const existingAttachmentIds = (await requestResult(
    attachmentStore.index("messageId").getAllKeys(message.id),
  )) as IDBValidKey[];
  for (const id of existingAttachmentIds) {
    if (!incomingAttachmentIds.has(String(id))) attachmentStore.delete(id);
  }
  transaction
    .objectStore("messages")
    .put(storedMessage(conversationId, message, order));
  for (const attachment of message.attachments ?? []) {
    attachmentStore.put({
      ...attachment,
      conversationId,
      messageId: message.id,
      createdAt: Date.now(),
    } satisfies StoredAttachment);
  }
  await transactionDone(transaction);
}

export async function replaceConversationMessages(
  conversation: Conversation,
): Promise<void> {
  const db = await openVesperDatabase();
  const transaction = db.transaction(
    ["conversations", "messages", "attachments"],
    "readwrite",
  );
  const messages = transaction.objectStore("messages");
  const attachments = transaction.objectStore("attachments");
  for (const key of (await requestResult(
    messages.index("conversationId").getAllKeys(conversation.id),
  )) as IDBValidKey[]) {
    messages.delete(key);
  }
  for (const key of (await requestResult(
    attachments.index("conversationId").getAllKeys(conversation.id),
  )) as IDBValidKey[]) {
    attachments.delete(key);
  }
  transaction.objectStore("conversations").put(conversationRecord(conversation));
  conversation.messages.forEach((message, order) => {
    messages.put(storedMessage(conversation.id, message, order));
    for (const attachment of message.attachments ?? []) {
      attachments.put({
        ...attachment,
        conversationId: conversation.id,
        messageId: message.id,
        createdAt: Date.now(),
      } satisfies StoredAttachment);
    }
  });
  await transactionDone(transaction);
}

export async function deleteConversationData(conversationId: string): Promise<void> {
  const db = await openVesperDatabase();
  const transaction = db.transaction(
    ["conversations", "messages", "attachments", "artifacts", "questionSessions"],
    "readwrite",
  );
  transaction.objectStore("conversations").delete(conversationId);
  for (const storeName of ["messages", "attachments", "artifacts"] as const) {
    const store = transaction.objectStore(storeName);
    const keys = (await requestResult(
      store.index("conversationId").getAllKeys(conversationId),
    )) as IDBValidKey[];
    for (const key of keys) store.delete(key);
  }
  const sessionKey = await requestResult(
    transaction
      .objectStore("questionSessions")
      .index("conversationId")
      .getKey(conversationId),
  );
  if (sessionKey !== undefined) {
    transaction.objectStore("questionSessions").delete(sessionKey);
  }
  await transactionDone(transaction);
}

export async function saveProject(project: Project): Promise<void> {
  const db = await openVesperDatabase();
  const transaction = db.transaction("projects", "readwrite");
  transaction.objectStore("projects").put(project);
  await transactionDone(transaction);
}

export async function deleteProjectData(projectId: string): Promise<void> {
  const db = await openVesperDatabase();
  const transaction = db.transaction(["projects", "conversations"], "readwrite");
  transaction.objectStore("projects").delete(projectId);
  const conversations = transaction.objectStore("conversations");
  const records = (await requestResult(
    conversations.index("projectId").getAll(projectId),
  )) as Conversation[];
  for (const conversation of records) {
    const { projectId: _projectId, ...next } = conversation;
    conversations.put(next);
  }
  await transactionDone(transaction);
}

export async function saveAssistant(assistant: AssistantPersona): Promise<void> {
  const db = await openVesperDatabase();
  const transaction = db.transaction("assistants", "readwrite");
  transaction.objectStore("assistants").put(assistant);
  await transactionDone(transaction);
}

export async function deleteAssistantData(id: string): Promise<void> {
  const db = await openVesperDatabase();
  const transaction = db.transaction("assistants", "readwrite");
  transaction.objectStore("assistants").delete(id);
  await transactionDone(transaction);
}

export async function saveQuestionSession(
  session: QuestionSession,
): Promise<void> {
  const db = await openVesperDatabase();
  const transaction = db.transaction("questionSessions", "readwrite");
  transaction.objectStore("questionSessions").put(session);
  await transactionDone(transaction);
}

export async function deleteQuestionSession(id: string): Promise<void> {
  const db = await openVesperDatabase();
  const transaction = db.transaction("questionSessions", "readwrite");
  transaction.objectStore("questionSessions").delete(id);
  await transactionDone(transaction);
}

export async function importArchive(archive: PortableArchive): Promise<void> {
  const db = await openVesperDatabase();
  const transaction = db.transaction([...STORE_NAMES], "readwrite");
  for (const name of STORE_NAMES) transaction.objectStore(name).clear();
  for (const conversation of archive.conversations) {
    transaction.objectStore("conversations").put(conversationRecord(conversation));
    conversation.messages.forEach((message, order) => {
      transaction
        .objectStore("messages")
        .put(storedMessage(conversation.id, message, order));
      for (const attachment of message.attachments ?? []) {
        transaction.objectStore("attachments").put({
          ...attachment,
          conversationId: conversation.id,
          messageId: message.id,
          createdAt: Date.now(),
        } satisfies StoredAttachment);
      }
    });
  }
  archive.artifacts?.forEach((artifact) =>
    transaction.objectStore("artifacts").put(artifact),
  );
  archive.projects.forEach((project) =>
    transaction.objectStore("projects").put(project),
  );
  archive.assistants.forEach((assistant) =>
    transaction.objectStore("assistants").put(normalizeAssistant(assistant)),
  );
  transaction.objectStore("settings").put({ id: "app", value: archive.settings });
  transaction
    .objectStore("agentConfigurations")
    .put({ id: "active", value: archive.settings.agentTeam });
  archive.questionSessions?.forEach((session) =>
    transaction.objectStore("questionSessions").put(session),
  );
  transaction.objectStore("metadata").put({
    id: "schema",
    version: DATABASE_VERSION,
    importedAt: Date.now(),
  });
  await transactionDone(transaction);
}

export async function exportDatabaseArchive(): Promise<PortableArchive> {
  const db = await openVesperDatabase();
  const transaction = db.transaction(
    ["conversations", "projects", "assistants", "settings", "questionSessions"],
    "readonly",
  );
  const [conversationRecords, projects, assistants, storedSetting, sessions] =
    await Promise.all([
      requestResult(transaction.objectStore("conversations").getAll()),
      requestResult(transaction.objectStore("projects").getAll()),
      requestResult(transaction.objectStore("assistants").getAll()),
      requestResult(transaction.objectStore("settings").get("app")),
      requestResult(transaction.objectStore("questionSessions").getAll()),
    ]);
  await transactionDone(transaction);
  const artifacts: Artifact[] = [];
  const conversations = await Promise.all(
    (conversationRecords as unknown[]).map(async (record) => {
      const conversation = readConversation(record);
      const loaded = await loadConversationMessages(conversation.id);
      artifacts.push(...loaded.artifacts);
      return {
        ...conversation,
        messages: await hydrateMessageAttachments(loaded.messages),
        messagesLoaded: true,
      };
    }),
  );
  return {
    format: "vesper-ai",
    version: 2,
    exportedAt: new Date().toISOString(),
    conversations,
    artifacts,
    projects: projects as Project[],
    assistants: (assistants as AssistantPersona[]).map(normalizeAssistant),
    settings: (storedSetting as StoredSetting | undefined)?.value ?? DEFAULT_SETTINGS,
    questionSessions: sessions as QuestionSession[],
  };
}

export async function deleteDatabase(): Promise<void> {
  if (databasePromise) {
    try {
      (await databasePromise).close();
    } catch {
      // The connection may already be closed.
    }
    databasePromise = undefined;
  }
  if (!globalThis.indexedDB) return;
  await new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DATABASE_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () =>
      reject(new Error(request.error?.message ?? "Cancellazione database fallita."));
    request.onblocked = () =>
      reject(
        new Error(
          "Cancellazione bloccata da un'altra scheda. Chiudi le altre schede e riprova.",
        ),
      );
  });
}

export async function inspectSchema(): Promise<Record<string, string[]>> {
  const db = await openVesperDatabase();
  const transaction = db.transaction([...STORE_NAMES], "readonly");
  return Object.fromEntries(
    STORE_NAMES.map((name: StoreName) => [
      name,
      Array.from(transaction.objectStore(name).indexNames),
    ]),
  );
}
